import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type {
  Addresses,
  AddressesAvailableSlotsListParams,
  AddressesExceptionsCreateImpactParams,
  AddressesExceptionsDeleteImpactParams,
  AddressesExceptionsListParams,
  AddressesExceptionsUpdateImpactParams,
  AddressesGatesBulkImpactParams,
  AddressesListParams,
  AddressesOperatingHoursBulkImpactParams,
  AddressesTimeSlotSettingsDeactivationPreviewParams,
  AddressesTimeSlotSettingsImpactParams,
  AddressTimeSlotSettingsWrite,
  Gate,
  OperatingHoursExceptionWrite,
  OperatingHoursDayWrite,
  TimeSlotSetup,
} from '@apiTypes'

/**
 * Settings writes hold the confirmation dialog, and cancel is ignored until the request settles.
 * A connection that never answers must still fail the save. 30s covers a normal write.
 */
export const SETTINGS_CHANGE_REQUEST_TIMEOUT_MS = 30_000

/**
 * A signal, not axios' `timeout`: `timeout` restarts on the post-refresh retry, while one signal
 * per call bounds the request, a token refresh, and the retry together.
 */
function settingsRequest() {
  return { signal: AbortSignal.timeout(SETTINGS_CHANGE_REQUEST_TIMEOUT_MS) }
}

export const useAddressesStore = defineStore('addresses', {
  actions: {
    fetchAddresses(query: AddressesListParams) {
      return api.addresses.addressesList(query)
    },
    fetchAddress(id: number) {
      return api.addresses.addressesRetrieve({ id })
    },
    addAddress(data: Addresses.AddressesCreate.RequestBody) {
      return api.addresses.addressesCreate(data)
    },
    updateAddress(id: number, params: Addresses.AddressesPartialUpdate.RequestBody) {
      return api.addresses.addressesPartialUpdate({ id }, params)
    },
    deleteAddress(id: number) {
      return api.addresses.addressesDestroy({ id })
    },
    fetchAddressDeactivationPreview(addressId: number, query: Omit<AddressesTimeSlotSettingsDeactivationPreviewParams, 'addressPk'> = {}) {
      return api.addresses.addressesTimeSlotSettingsDeactivationPreview({ addressPk: addressId, ...query })
    },
    fetchTimeSlotSettings(addressId: number) {
      return api.addresses.addressesTimeSlotSettingsList({ addressPk: addressId })
    },
    createTimeSlotSettings(addressId: number, payload: AddressTimeSlotSettingsWrite) {
      return api.addresses.addressesTimeSlotSettingsCreate({ addressPk: addressId }, payload, settingsRequest())
    },
    updateTimeSlotSettings(addressId: number, payload: AddressTimeSlotSettingsWrite) {
      return api.addresses.addressesTimeSlotSettingsUpdate({ addressPk: addressId }, payload, settingsRequest())
    },
    fetchTimeSlotSettingsImpact(addressId: number, payload: AddressTimeSlotSettingsWrite, query: Omit<AddressesTimeSlotSettingsImpactParams, 'addressPk'>) {
      return api.addresses.addressesTimeSlotSettingsImpact({ addressPk: addressId, ...query }, payload, settingsRequest())
    },
    setupTimeSlots(addressId: number, payload: TimeSlotSetup) {
      return api.addresses.addressesTimeSlotSettingsSetupCreate({ addressPk: addressId }, payload, settingsRequest())
    },
    turnOffTimeSlots(addressId: number) {
      return api.addresses.addressesTimeSlotSettingsTurnOff({ addressPk: addressId }, settingsRequest())
    },
    turnOnTimeSlots(addressId: number) {
      return api.addresses.addressesTimeSlotSettingsTurnOn({ addressPk: addressId }, settingsRequest())
    },
    fetchGates(addressId: number) {
      return api.addresses.addressesGatesList({ addressPk: addressId })
    },
    fetchAvailableSlots(addressId: number, query: Omit<AddressesAvailableSlotsListParams, 'addressPk'>) {
      return api.addresses.addressesAvailableSlotsList({ addressPk: addressId, ...query })
    },
    fetchOperatingHours(addressId: number) {
      return api.addresses.addressesOperatingHoursList({ addressPk: addressId })
    },
    saveGates(addressId: number, gates: Gate[]) {
      return api.addresses.addressesGatesBulkUpdate({ addressPk: addressId }, { gates }, settingsRequest())
    },
    fetchGatesImpact(addressId: number, gates: Gate[], query: Omit<AddressesGatesBulkImpactParams, 'addressPk'>) {
      return api.addresses.addressesGatesBulkImpact({ addressPk: addressId, ...query }, { gates }, settingsRequest())
    },
    saveOperatingHours(addressId: number, days: OperatingHoursDayWrite[]) {
      return api.addresses.addressesOperatingHoursBulkUpdate({ addressPk: addressId }, { days }, settingsRequest())
    },
    fetchOperatingHoursImpact(addressId: number, days: OperatingHoursDayWrite[], query: Omit<AddressesOperatingHoursBulkImpactParams, 'addressPk'>) {
      return api.addresses.addressesOperatingHoursBulkImpact({ addressPk: addressId, ...query }, { days }, settingsRequest())
    },
    fetchExceptions(query: AddressesExceptionsListParams) {
      return api.addresses.addressesExceptionsList(query)
    },
    createException(addressId: number, payload: OperatingHoursExceptionWrite) {
      return api.addresses.addressesExceptionsCreate({ addressPk: addressId }, payload, settingsRequest())
    },
    fetchExceptionCreateImpact(addressId: number, payload: OperatingHoursExceptionWrite, query: Omit<AddressesExceptionsCreateImpactParams, 'addressPk'>) {
      return api.addresses.addressesExceptionsCreateImpact({ addressPk: addressId, ...query }, payload, settingsRequest())
    },
    updateException(addressId: number, id: number, payload: OperatingHoursExceptionWrite) {
      return api.addresses.addressesExceptionsUpdate({ addressPk: addressId, id }, payload, settingsRequest())
    },
    fetchExceptionUpdateImpact(addressId: number, id: number, payload: OperatingHoursExceptionWrite, query: Omit<AddressesExceptionsUpdateImpactParams, 'addressPk' | 'id'>) {
      return api.addresses.addressesExceptionsUpdateImpact({ addressPk: addressId, id, ...query }, payload, settingsRequest())
    },
    deleteException(addressId: number, id: number) {
      return api.addresses.addressesExceptionsDestroy({ addressPk: addressId, id }, settingsRequest())
    },
    fetchExceptionDeleteImpact(addressId: number, id: number, query: Omit<AddressesExceptionsDeleteImpactParams, 'addressPk' | 'id'>) {
      return api.addresses.addressesExceptionsDeleteImpact({ addressPk: addressId, id, ...query }, settingsRequest())
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useAddressesStore, import.meta.hot))
